import React, { createContext, useState, useContext, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../auth/AuthContext';
import { usePractice } from './PracticeContext';

const CallStateContext = createContext(null);

// Hand a socket event to every subscriber. One bad subscriber must not kill the
// others or the socket handler.
const notifyAll = (subscribers, label, payload) => {
  subscribers.forEach((cb) => {
    try {
      cb(payload);
    } catch (err) {
      console.error(`[WS] ${label} subscriber failed:`, err);
    }
  });
};

export const CallStateProvider = ({ children }) => {
  const { keycloak } = useAuth();
  const { practiceId } = usePractice();
  const [activeCalls, setActiveCalls] = useState([]);
  const [activeCall, setActiveCall] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const isClosingRef = useRef(false); // Track intentional close

  // Config live-sync subscribers (see hooks/useConfigSync.js).
  // This provider wraps every route, so its socket is the one connection every
  // logged-in tab already holds — including tabs sitting on the Konfiguration
  // page, which open no socket of their own. That makes it the right carrier for
  // `config_changed` and means the feature adds no new connection, auth path or
  // reconnect logic. Kept in a ref (not state) so adding a subscriber never
  // re-renders the whole app.
  const configSubscribersRef = useRef(new Set());
  const subscribeConfigEvents = useCallback((cb) => {
    configSubscribersRef.current.add(cb);
    return () => configSubscribersRef.current.delete(cb);
  }, []);

  // Same carrier, same reasoning, for new-task alerts (see useTaskAlerts).
  const taskSubscribersRef = useRef(new Set());
  const subscribeTaskAlerts = useCallback((cb) => {
    taskSubscribersRef.current.add(cb);
    return () => taskSubscribersRef.current.delete(cb);
  }, []);

  // Same carrier for the support chat (see components/support/SupportProvider.js). The event
  // carries ids only, never message text. Subscribers also get `{resync: true}`
  // on every initial_state: the backend drops events while no socket is open and
  // nothing replays them, so a (re)connect is the moment to re-read.
  const supportSubscribersRef = useRef(new Set());
  const subscribeSupportEvents = useCallback((cb) => {
    supportSubscribersRef.current.add(cb);
    return () => supportSubscribersRef.current.delete(cb);
  }, []);

  const connect = useCallback(() => {
    // Prevent multiple connections
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
      console.log('[WS] Connection already exists, skipping');
      return;
    }

    // Backend requires both practice_id and token query params (Session 4/5).
    if (!practiceId || !keycloak?.token) {
      console.log('[WS] Waiting for practiceId and token before connecting');
      return;
    }

    // Build WebSocket URL from API URL.
    // Anchor both edits: an unanchored `.replace('/api','')` corrupts hosts that
    // start with "api" (api.deskbot.io -> the "//api" matches first -> broken
    // "wss:/.deskbot.io/api"). Swap the scheme at the START, strip "/api" at the END.
    const apiUrl = process.env.REACT_APP_API_URL || '/api';
    const wsBase = apiUrl
      .replace(/^http/, 'ws')        // https://... -> wss://... ; http://... -> ws://...
      .replace(/\/api\/?$/, '');     // strip trailing /api (or /api/) only
    const wsUrl = `${wsBase}/ws/call-monitor?practice_id=${encodeURIComponent(practiceId)}&token=${encodeURIComponent(keycloak.token)}`;

    console.log('[WS] Connecting to:', wsBase + '/ws/call-monitor?practice_id=...');
    isClosingRef.current = false;
    wsRef.current = new WebSocket(wsUrl);

    wsRef.current.onopen = () => {
      console.log('[WS] Connected to call monitor');
      setIsConnected(true);
      setError('');
      reconnectAttempts.current = 0;
    };

    wsRef.current.onmessage = (event) => {
      const { type, data } = JSON.parse(event.data);
      setLastUpdate(new Date());

      switch (type) {
        case 'initial_state':
          console.log('[WS] Received initial state:', data.active_calls?.length, 'calls');
          setActiveCalls(data.active_calls || []);
          if (data.active_calls?.length > 0) {
            const firstCall = data.active_calls[0];
            setActiveCall(firstCall);
            // Filter to user/assistant messages only
            const filtered = (firstCall.conversation_history || []).filter(
              msg => msg.role === 'user' || msg.role === 'assistant'
            );
            setConversation(filtered);
          } else {
            setActiveCall(null);
            setConversation([]);
          }
          notifyAll(supportSubscribersRef.current, 'support_changed', { resync: true });
          break;

        case 'call_started':
          console.log('[WS] Call started:', data.session_id);
          setActiveCalls(prev => [...prev, data]);
          // If no active call, set this one and clear conversation
          setActiveCall(prev => {
            if (!prev) {
              setConversation([]);
              return data;
            }
            return prev;
          });
          break;

        case 'message_added':
          console.log('[WS] Message added:', data.message?.role, 'at index:', data.index);
          // Update activeCalls - place message at correct index
          setActiveCalls(prev => prev.map(call => {
            if (call.session_id !== data.session_id) return call;
            // Consent latch: the caller refused, so drop the line. The backend gate
            // stops later turns, but consent_processor_node and the parallel
            // cached-response node race within the SAME turn, so the ack can arrive
            // just after the wipe. Dropping it here needs no backend ordering.
            if (call.transcript_suppressed) return call;
            const history = [...(call.conversation_history || [])];
            // Place at the backend's index. Gaps (lines that are never broadcast, e.g. the
            // 🧭/🔄 notes) stay null so later indexes still line up and a repeat overwrites.
            while (history.length <= data.index) history.push(null);
            history[data.index] = data.message;
            return { ...call, conversation_history: history };
          }));
          // Update activeCall if it matches
          setActiveCall(prev => {
            if (prev?.session_id !== data.session_id) return prev;
            if (prev.transcript_suppressed) return prev;  // see consent latch above
            const history = [...(prev.conversation_history || [])];
            // Place at the backend's index (gaps stay null, see above)
            while (history.length <= data.index) history.push(null);
            history[data.index] = data.message;
            const filtered = history.filter(
              msg => msg && (msg.role === 'user' || msg.role === 'assistant')
            );
            setConversation(filtered);
            return { ...prev, conversation_history: history };
          });
          break;

        case 'transcript_suppressed':
          // The caller explicitly refused recording. The call stays listed and
          // takeover stays available; only the transcript is withheld. Wipes what
          // the browser already holds, since the backend read gate can only stop
          // lines that have not been sent yet.
          console.log('[WS] Transcript suppressed (no consent):', data.session_id);
          setActiveCalls(prev => prev.map(call => (
            call.session_id === data.session_id
              ? { ...call, conversation_history: [], transcript_suppressed: true }
              : call
          )));
          setActiveCall(prev => {
            if (prev?.session_id !== data.session_id) return prev;
            setConversation([]);
            return { ...prev, conversation_history: [], transcript_suppressed: true };
          });
          break;

        case 'call_ended':
          console.log('[WS] Call ended:', data.session_id);
          setActiveCalls(prev => prev.filter(call => call.session_id !== data.session_id));
          setActiveCall(prev => {
            if (prev?.session_id === data.session_id) {
              setConversation([]);
              return null;
            }
            return prev;
          });
          break;

        case 'config_changed':
          // Someone changed this practice's configuration. Carries a scope tag
          // only — never config values — so subscribers re-read through the normal
          // authenticated GET. Errors in one subscriber must not kill the others
          // or the socket handler.
          console.log('[WS] Config changed:', data?.scope, 'from', data?.origin);
          configSubscribersRef.current.forEach((cb) => {
            try {
              cb(data);
            } catch (err) {
              console.error('[WS] config_changed subscriber failed:', err);
            }
          });
          break;

        case 'task_alert':
          // A new task was created. Mirrors config_changed: this socket is the
          // only one open on every page, so it is what makes the alert
          // page-independent. Same isolation — one bad subscriber must not kill
          // the others or the socket handler.
          taskSubscribersRef.current.forEach((cb) => {
            try {
              cb(data);
            } catch (err) {
              console.error('[WS] task_alert subscriber failed:', err);
            }
          });
          break;

        case 'support_changed':
          // {conversation_id} for a message/close change, {link: true} for a
          // link change. Subscribers re-read through the authenticated API.
          notifyAll(supportSubscribersRef.current, 'support_changed', data || {});
          break;

        default:
          console.log('[WS] Unknown event type:', type);
      }
    };

    wsRef.current.onclose = () => {
      console.log('[WS] Disconnected');
      setIsConnected(false);

      // Don't reconnect if we're intentionally closing
      if (isClosingRef.current) {
        console.log('[WS] Intentional close, not reconnecting');
        return;
      }

      // Exponential backoff reconnection (1s, 2s, 4s, 8s... max 30s)
      const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
      reconnectAttempts.current++;

      console.log(`[WS] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current})`);
      reconnectTimeoutRef.current = setTimeout(connect, delay);
    };

    wsRef.current.onerror = (err) => {
      console.error('[WS] Error:', err);
      setError('WebSocket connection error');
    };
  }, [practiceId, keycloak]);

  useEffect(() => {
    connect();

    return () => {
      isClosingRef.current = true; // Signal intentional close - prevents reconnection
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connect]);

  // Session 8 Gotcha #4: defense-in-depth client-side filter on activeCalls.
  // The WebSocket connection is per-practice (reconnects on practiceId change),
  // but for the brief window between practice switch and WS reconnect, filter
  // by event payload's practice_id so stale calls from the previous practice
  // never render.
  const visibleCalls = useMemo(() => {
    if (!practiceId) return [];
    return activeCalls.filter(c => !c.practice_id || c.practice_id === practiceId);
  }, [activeCalls, practiceId]);

  const value = {
    activeCalls: visibleCalls,
    activeCall,
    setActiveCall,
    conversation,
    isConnected,
    error,
    setError,
    lastUpdate,
    subscribeConfigEvents,
    subscribeTaskAlerts,
    subscribeSupportEvents,
  };

  return (
    <CallStateContext.Provider value={value}>
      {children}
    </CallStateContext.Provider>
  );
};

export const useCallState = () => {
  const context = useContext(CallStateContext);
  if (context === null) {
    throw new Error('useCallState must be used within a CallStateProvider');
  }
  return context;
};

export default CallStateContext;
